import { CircleSolid } from '@carbon/icons-react';
import styled from '@emotion/styled';
import axios from 'axios';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useHarmonicIntervalFn } from 'react-use';

import {
  Option,
  OptionLabel,
  OptionWrapper
} from 'components/MainLayout/styled';
import { Status, StatusIndicator } from 'components/statusPage/types';
import { STATUS_PAGE_POLLING_INTERVAL } from 'utils/constants';

const StatusCircle = styled(CircleSolid)`
  color: var(--success);

  &[data-status='error'] {
    color: var(--error);
  }
`;

export function ServiceStatusOption(): JSX.Element {
  const [t] = useTranslation();
  const [isOnline, setIsOnline] = React.useState(true);
  const [serviceStatus, setServiceStatus] = React.useState(
    StatusIndicator.None
  );

  const fetchServiceStatus = React.useCallback(() => {
    axios
      .get<{ status: Status }>(`${CONFIG.statusPageUrl}/api/v2/status.json`)
      .then(({ data }) => {
        const { status } = data;
        setServiceStatus(status.indicator);
      });
  }, []);

  React.useEffect(() => {
    if (isOnline) {
      fetchServiceStatus();
    }
  }, []);

  React.useEffect(() => {
    window.addEventListener('offline', () => {
      setIsOnline(false);
    });

    window.addEventListener('online', () => {
      fetchServiceStatus();
      setIsOnline(true);
    });
  }, [fetchServiceStatus]);

  useHarmonicIntervalFn(async () => {
    if (isOnline) {
      fetchServiceStatus();
    }
  }, STATUS_PAGE_POLLING_INTERVAL);

  return (
    <Option>
      <a href={CONFIG.statusPageUrl} target="_blank" rel="noopener noreferrer">
        <OptionWrapper data-theme="dark">
          <StatusCircle
            size={32}
            data-status={
              serviceStatus === StatusIndicator.None ? 'success' : 'error'
            }
          />
          <OptionLabel>{t('labels.service_status')}</OptionLabel>
        </OptionWrapper>
      </a>
    </Option>
  );
}
